/**
 * Firebase API Service for OilWellX
 * Replaces Supabase Edge Functions with Firebase Firestore/Functions calls
 */

import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase/client';
import {
  queryDocuments,
  createDocument,
  updateDocument,
  setDocument,
  deleteDocument,
  getDocument,
  getDocumentsByTenant
} from '../firebase/database';
import { isoToEntryDateId } from '../dateUtils';

/**
 * Get current user's ID token for authenticated requests
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return await currentUser.getIdToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Call a Firebase Cloud Function
 */
async function callFunction<T = any>(functionName: string, data?: any): Promise<T | null> {
  try {
    const callable = httpsCallable(functions, functionName);
    const result = await callable(data);
    return result.data as T;
  } catch (error) {
    console.error(`Error calling function ${functionName}:`, error);
    return null;
  }
}

// ==================== WELLS API ====================

export async function fetchWells(companyId: string) {
  return getDocumentsByTenant('wells', companyId);
}

export async function createWell(companyId: string, wellData: any) {
  return createDocument('wells', { ...wellData, companyId });
}

export async function updateWell(wellId: string, wellData: any) {
  return updateDocument('wells', wellId, wellData);
}

export async function deleteWell(wellId: string) {
  return deleteDocument('wells', wellId);
}

export async function getWellById(wellId: string) {
  return getDocument('wells', wellId);
}

// ==================== PRODUCTION DATA API ====================

export async function fetchProductionData(companyId: string, wellId?: string) {
  const filters = [{ field: 'companyId', operator: '==', value: companyId }];
  if (wellId) {
    filters.push({ field: 'wellId', operator: '==', value: wellId });
  }
  return queryDocuments('production_data', filters as any, 'date', 'desc');
}

export async function createProductionEntry(companyId: string, data: any) {
  return createDocument('production_data', { ...data, companyId });
}

export async function updateProductionEntry(entryId: string, data: any) {
  return updateDocument('production_data', entryId, data);
}

export async function deleteProductionEntry(entryId: string) {
  return deleteDocument('production_data', entryId);
}

// ==================== USERS API ====================

export async function fetchUsers(companyId: string) {
  return getDocumentsByTenant('users', companyId);
}

export async function createUser(userData: any) {
  // This would typically call a Firebase Cloud Function to create auth user + firestore doc
  return callFunction('createUser', userData);
}

export async function updateUser(userId: string, userData: any) {
  return updateDocument('users', userId, userData);
}

export async function deleteUser(userId: string) {
  // This would typically call a Firebase Cloud Function to delete auth user + firestore doc
  return callFunction('deleteUser', { userId });
}

// ==================== WELL NOTES API ====================

export async function fetchWellNotes(companyId: string, wellId: string) {
  if (!companyId || !wellId) return [];
  try {
    const collectionPath = `companies/${companyId}/wells/${wellId}/notes`;
    // Using queryDocuments with path
    return await queryDocuments(collectionPath, [], 'createdAt', 'desc');
  } catch (error) {
    // Gracefully handle permission errors - notes are non-critical
    return [];
  }
}

export async function createWellNote(companyId: string, wellId: string, noteData: any) {
  if (!companyId || !wellId) throw new Error('Missing companyId or wellId');
  const collectionPath = `companies/${companyId}/wells/${wellId}/notes`;
  return createDocument(collectionPath, {
    ...noteData,
    companyId,
    wellId,
    createdAt: new Date().toISOString()
  });
}

// ==================== INVOICES API ====================

export async function fetchInvoices(companyId: string) {
  return getDocumentsByTenant('invoices', companyId);
}

export async function createInvoice(companyId: string, invoiceData: any) {
  return createDocument('invoices', { ...invoiceData, companyId });
}

export async function updateInvoice(invoiceId: string, invoiceData: any) {
  return updateDocument('invoices', invoiceId, invoiceData);
}

export async function deleteInvoice(invoiceId: string) {
  return deleteDocument('invoices', invoiceId);
}

export async function approveInvoice(invoiceId: string, approvedBy: string) {
  return updateDocument('invoices', invoiceId, {
    status: 'approved',
    approvedBy,
    approvedAt: new Date().toISOString()
  });
}

// ==================== NOTIFICATIONS API ====================

export async function fetchNotifications(companyId: string, userId?: string) {
  // Use the nested collection path: companies/{companyId}/notifications
  const collectionPath = `companies/${companyId}/notifications`;

  // We can't use queryDocuments directly because it takes a collection name, not a path
  // So we'll use the underlying Firestore functions directly or update queryDocuments
  // But queryDocuments in database.ts takes a collectionName which is passed to collection(db, collectionName)
  // If we pass a path with slashes, it might work if collection() supports it (it does in v9 modular SDK)

  const filters: any[] = [{ field: 'companyId', operator: '==', value: companyId }];
  if (userId) {
    filters.push({ field: 'userId', operator: '==', value: userId });
  }

  // Pass the full path to queryDocuments
  return queryDocuments(collectionPath, filters, 'createdAt', 'desc');
}

export async function createNotification(companyId: string, notification: any) {
  const collectionPath = `companies/${companyId}/notifications`;
  return createDocument(collectionPath, { ...notification, companyId });
}

export async function markNotificationAsRead(notificationId: string) {
  // We need the companyId to construct the path. 
  // This function signature is problematic because it doesn't take companyId.
  // We'll try to get it from context, but this is risky if called outside of context.
  // Ideally, we should pass companyId to this function.
  // For now, let's try to find the notification in the current tenant's collection.

  // Import dynamically to avoid circular dependency if possible, or just use the helper
  // But we can't import getCompanyId here easily without potential cycles.
  // Let's assume the caller handles the path or we update the signature.

  // Since we can't easily change the signature everywhere, let's try to use the current company from window/storage
  // This is a hack, but necessary without refactoring everything.
  let companyId = 'default';
  try {
    const session = localStorage.getItem('oilwellx_auth_session') || sessionStorage.getItem('oilwellx_auth_session');
    if (session) {
      const user = JSON.parse(session);
      companyId = user.companyId || 'default';
    }
  } catch (e) { }

  const collectionPath = `companies/${companyId}/notifications`;
  return updateDocument(collectionPath, notificationId, {
    read: true,
    readAt: new Date().toISOString()
  });
}

export async function deleteNotification(notificationId: string) {
  let companyId = 'default';
  try {
    const session = localStorage.getItem('oilwellx_auth_session') || sessionStorage.getItem('oilwellx_auth_session');
    if (session) {
      const user = JSON.parse(session);
      companyId = user.companyId || 'default';
    }
  } catch (e) { }

  const collectionPath = `companies/${companyId}/notifications`;
  return deleteDocument(collectionPath, notificationId);
}

// ==================== COMPANIES/TENANTS API ====================

export async function fetchCompanies() {
  return queryDocuments('companies');
}

export async function createCompany(companyData: any) {
  return callFunction('createCompany', companyData);
}

export async function updateCompany(companyId: string, companyData: any) {
  return updateDocument('companies', companyId, companyData);
}

export async function deleteCompany(companyId: string) {
  return callFunction('deleteCompany', { companyId });
}

// ==================== TIME CLOCK API ====================

export async function fetchTimeEntries(companyId: string, userId?: string) {
  const filters: any[] = [{ field: 'companyId', operator: '==', value: companyId }];
  if (userId) {
    filters.push({ field: 'userId', operator: '==', value: userId });
  }
  return queryDocuments('time_entries', filters, 'clockIn', 'desc');
}

export async function clockIn(companyId: string, userId: string, location?: any) {
  return createDocument('time_entries', {
    companyId,
    userId,
    clockIn: new Date().toISOString(),
    clockInLocation: location,
    status: 'active'
  });
}

export async function clockOut(entryId: string, location?: any, notes?: string, mileage?: number) {
  return updateDocument('time_entries', entryId, {
    clockOut: new Date().toISOString(),
    clockOutLocation: location,
    notes,
    mileage,
    status: 'completed'
  });
}

export async function getActiveTimeEntry(companyId: string, userId: string) {
  const entries = await queryDocuments('time_entries', [
    { field: 'companyId', operator: '==', value: companyId },
    { field: 'userId', operator: '==', value: userId },
    { field: 'status', operator: '==', value: 'active' }
  ] as any, 'clockIn', 'desc');
  return entries?.[0] || null;
}

// ==================== SHIFT HANDOFFS API ====================

export async function fetchShiftHandoffs(companyId: string) {
  return queryDocuments('shift_handoffs', [
    { field: 'companyId', operator: '==', value: companyId }
  ] as any, 'createdAt', 'desc');
}

export async function getActiveHandoff(companyId: string, userId: string) {
  const handoffs = await queryDocuments('shift_handoffs', [
    { field: 'companyId', operator: '==', value: companyId },
    { field: 'fromUserId', operator: '==', value: userId },
    { field: 'submitted', operator: '==', value: false }
  ] as any, 'createdAt', 'desc');
  return handoffs?.[0] || null;
}

export async function createShiftHandoff(companyId: string, handoffData: any) {
  return createDocument('shift_handoffs', {
    ...handoffData,
    companyId,
    submitted: false,
    createdAt: new Date().toISOString()
  });
}

export async function updateShiftHandoff(handoffId: string, handoffData: any) {
  return updateDocument('shift_handoffs', handoffId, handoffData);
}

export async function submitShiftHandoff(handoffId: string, toUserName: string) {
  return updateDocument('shift_handoffs', handoffId, {
    submitted: true,
    submittedAt: new Date().toISOString(),
    toUserName
  });
}

// ==================== TASKS API ====================

export async function fetchTasks(companyId: string, filters?: { status?: string; assignedTo?: string }) {
  const queryFilters: any[] = [{ field: 'companyId', operator: '==', value: companyId }];
  if (filters?.status) {
    queryFilters.push({ field: 'status', operator: '==', value: filters.status });
  }
  if (filters?.assignedTo) {
    queryFilters.push({ field: 'assignedTo', operator: '==', value: filters.assignedTo });
  }
  return queryDocuments('tasks', queryFilters, 'createdAt', 'desc');
}

export async function createTask(companyId: string, taskData: any) {
  return createDocument('tasks', {
    ...taskData,
    companyId,
    status: taskData.status || 'pending',
    createdAt: new Date().toISOString()
  });
}

export async function updateTask(taskId: string, taskData: any) {
  return updateDocument('tasks', taskId, {
    ...taskData,
    updatedAt: new Date().toISOString()
  });
}

export async function deleteTask(taskId: string) {
  return deleteDocument('tasks', taskId);
}

// ==================== PRODUCTION ENTRIES API ====================

export async function createWellEntry(companyId: string, entryData: any) {
  if (!companyId) {
    throw new Error('Company ID is required when creating production entries.');
  }

  if (!entryData?.wellId) {
    throw new Error('Well ID is required when creating production entries.');
  }

  const collectionPath = `companies/${companyId}/wells/${entryData.wellId}/production_entries`;
  const result = await createDocument(collectionPath, {
    ...entryData,
    companyId,
    wellId: entryData.wellId,
  });

  // =========================================================================
  // ALERT CHECK - Analyze entry for potential alerts
  // =========================================================================
  try {
    const { checkWellForAlerts } = await import('../alerts');

    const well = {
      id: entryData.wellId,
      name: entryData.wellName || `Well ${entryData.wellId}`,
      tankCapacity: entryData.tankCapacity,
      lastMaintenance: entryData.lastMaintenance,
      maintenanceInterval: entryData.maintenanceInterval,
      assignedPumperIds: entryData.assignedPumperIds || [],
      managerIds: entryData.managerIds || [],
    };

    const alerts = await checkWellForAlerts(well, entryData);
    if (alerts.length > 0) {
    }
  } catch (alertError) {
  }

  return result;
}

export async function fetchWellEntries(companyId: string, wellId: string, limit?: number) {
  if (!companyId || !wellId) {
    return [];
  }

  const collectionPath = `companies/${companyId}/wells/${wellId}/production_entries`;
  return queryDocuments(collectionPath, [], 'timestamp', 'desc', limit);
}

/**
 * Upsert (create or update) a production entry with unique constraint
 * Uses YYYYMMDD format for document ID to enable lexicographic sorting
 * 
 * Document ID format: YYYYMMDD (e.g., "20251210" for December 10, 2025)
 * Collection path: companies/{companyId}/wells/{wellId}/production_entries/{YYYYMMDD}
 * 
 * @param companyId - Company identifier
 * @param wellId - Well identifier
 * @param entryDate - Production date in YYYY-MM-DD format
 * @param entryData - Production entry data
 * @returns Promise with created/updated entry
 */
export async function upsertWellEntry(
  companyId: string,
  wellId: string,
  entryDate: string,
  entryData: any
) {
  if (!companyId) {
    throw new Error('Company ID is required when upserting production entries.');
  }

  if (!wellId) {
    throw new Error('Well ID is required when upserting production entries.');
  }

  if (!entryDate) {
    throw new Error('Entry date is required when upserting production entries.');
  }

  // Convert ISO date (YYYY-MM-DD) to YYYYMMDD for document ID
  // This enables lexicographic sorting of entries by date
  const entryId = isoToEntryDateId(entryDate);

  const collectionPath = `companies/${companyId}/wells/${wellId}/production_entries`;

  // Import Firestore functions dynamically
  const { db } = await import('../firebase/client');
  const { doc, setDoc } = await import('firebase/firestore');

  const docRef = doc(db, collectionPath, entryId);

  const timestamp = new Date().toISOString();

  // Prepare entry data with all required fields
  const fullEntryData = {
    ...entryData,
    id: entryId,
    companyId,
    wellId,
    entryDate,
    updatedAt: timestamp,
    // Keep original createdAt if it exists, otherwise set to now
    createdAt: entryData.createdAt || timestamp,
  };

  // Use setDoc with merge to upsert (create if not exists, update if exists)
  await setDoc(docRef, fullEntryData, { merge: true });


  // =========================================================================
  // UPDATE WELL'S LAST ENTRY - So dashboard shows "last checked" correctly
  // =========================================================================
  try {
    const { updateDoc } = await import('firebase/firestore');
    const wellDocRef = doc(db, `companies/${companyId}/wells`, wellId);
    await updateDoc(wellDocRef, {
      lastEntry: entryDate,
      lastEntryTimestamp: timestamp,
      lastCheckedBy: entryData.userId || null,
      updatedAt: timestamp,
    });

    // Also update the production metadata
    const metadataRef = doc(db, `companies/${companyId}/wells/${wellId}/production_entries`, '_metadata');
    await setDoc(metadataRef, {
      lastEntryDate: entryDate,
      updatedAt: timestamp,
    }, { merge: true });
  } catch (wellUpdateError) {
    // Don't fail the save if well update fails
  }

  // =========================================================================
  // ALERT CHECK - Analyze entry for potential alerts
  // =========================================================================
  try {
    const { checkWellForAlerts } = await import('../alerts');

    // Construct a minimal well object for alert checking
    const well = {
      id: wellId,
      name: entryData.wellName || `Well ${wellId}`,
      tankCapacity: entryData.tankCapacity,
      lastMaintenance: entryData.lastMaintenance,
      maintenanceInterval: entryData.maintenanceInterval,
      assignedPumperIds: entryData.assignedPumperIds || [],
      managerIds: entryData.managerIds || [],
    };

    // Check for alerts based on this new entry
    const alerts = await checkWellForAlerts(well, fullEntryData);
    if (alerts.length > 0) {
    }
  } catch (alertError) {
    // Don't fail the save if alert check fails
  }

  return fullEntryData;
}

/**
 * Fetch a specific production entry by well ID and date
 * Uses YYYYMMDD document ID format for lookup
 * 
 * @param companyId - Company identifier
 * @param wellId - Well identifier  
 * @param entryDate - Production date in YYYY-MM-DD format
 * @returns Promise with entry data or null if not found
 */
export async function fetchWellEntryByDate(
  companyId: string,
  wellId: string,
  entryDate: string
) {
  if (!companyId || !wellId || !entryDate) {
    return null;
  }

  // Convert ISO date (YYYY-MM-DD) to YYYYMMDD for document ID lookup
  const entryId = isoToEntryDateId(entryDate);
  const collectionPath = `companies/${companyId}/wells/${wellId}/production_entries`;

  try {
    return await getDocument(collectionPath, entryId);
  } catch (error) {
    return null;
  }
}

// ==================== HAUL HISTORY API ====================

/**
 * Haul history entry interface
 * Stored at: companies/{companyId}/wells/{wellId}/haul_history/{YYYYMMDD_type_seq}
 */
export interface HaulHistoryEntry {
  id: string;
  date: string; // YYYY-MM-DD
  type: 'oil' | 'water';
  vendorId: string;
  vendorName: string;
  barrelsBefore: number;
  barrelsAfter: number;
  barrelsHauled: number;
  ticketNumber?: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
  sequence: number; // For multiple hauls same day
}

/**
 * Fetch haul history for a well on a specific date
 * Returns all oil and water haul entries for the given date
 * 
 * @param companyId - Company identifier
 * @param wellId - Well identifier
 * @param date - Date in YYYY-MM-DD format
 * @returns Promise with haul entries array
 */
export async function fetchHaulHistory(
  companyId: string,
  wellId: string,
  date: string
): Promise<{ oil: HaulHistoryEntry[]; water: HaulHistoryEntry[] }> {
  if (!companyId || !wellId || !date) {
    return { oil: [], water: [] };
  }

  const collectionPath = `companies/${companyId}/wells/${wellId}/haul_history`;
  const datePrefix = date.replace(/-/g, ''); // YYYYMMDD

  try {
    // Query all haul entries that start with this date
    const allHauls = await queryDocuments(
      collectionPath,
      [{ field: 'date', operator: '==', value: date }],
      'createdAt',
      'asc'
    );

    // Separate by type
    const oilHauls: HaulHistoryEntry[] = [];
    const waterHauls: HaulHistoryEntry[] = [];

    for (const haul of (allHauls || [])) {
      if (haul.type === 'oil') {
        oilHauls.push(haul as HaulHistoryEntry);
      } else if (haul.type === 'water') {
        waterHauls.push(haul as HaulHistoryEntry);
      }
    }

    return { oil: oilHauls, water: waterHauls };
  } catch (error) {
    return { oil: [], water: [] };
  }
}

/**
 * Get total barrels hauled for a well on a specific date
 * Convenience method for production calculations
 * 
 * @param companyId - Company identifier
 * @param wellId - Well identifier
 * @param date - Date in YYYY-MM-DD format
 * @returns Object with oilBBL and waterBBL totals
 */
export async function getHaulTotals(
  companyId: string,
  wellId: string,
  date: string
): Promise<{ oilBBL: number; waterBBL: number }> {
  const hauls = await fetchHaulHistory(companyId, wellId, date);
  
  const oilBBL = hauls.oil.reduce((sum, h) => sum + (h.barrelsHauled || 0), 0);
  const waterBBL = hauls.water.reduce((sum, h) => sum + (h.barrelsHauled || 0), 0);
  
  return { oilBBL, waterBBL };
}

/**
 * Create a haul history entry
 * Document ID format: YYYYMMDD_type_sequence (e.g., 20251210_oil_001)
 * 
 * @param companyId - Company identifier
 * @param wellId - Well identifier
 * @param haulData - Haul entry data
 * @returns Promise with created document
 */
export async function createHaulHistoryEntry(
  companyId: string,
  wellId: string,
  haulData: Omit<HaulHistoryEntry, 'id' | 'sequence'>
): Promise<HaulHistoryEntry | null> {
  if (!companyId || !wellId || !haulData.date) {
    return null;
  }

  const collectionPath = `companies/${companyId}/wells/${wellId}/haul_history`;
  const dateStr = haulData.date.replace(/-/g, ''); // YYYYMMDD

  try {
    // Get existing hauls for this date to determine sequence
    const existing = await fetchHaulHistory(companyId, wellId, haulData.date);
    const typeHauls = haulData.type === 'oil' ? existing.oil : existing.water;
    const sequence = typeHauls.length + 1;
    
    // Generate document ID: YYYYMMDD_type_XXX
    const docId = `${dateStr}_${haulData.type}_${String(sequence).padStart(3, '0')}`;
    
    const entryWithId: HaulHistoryEntry = {
      ...haulData,
      id: docId,
      sequence,
    };

    await setDocument(collectionPath, docId, entryWithId, false);
    return entryWithId;
  } catch (error) {
    console.error('Failed to create haul history entry:', error);
    return null;
  }
}

// ==================== PRODCUTION DRAFT API ====================

export async function updateDraftEntryField(companyId: string, wellId: string, draftId: string, updateData: any) {
  const collectionPath = `companies/${companyId}/wells/${wellId}/draft_entries`;
  // Use setDocument with merge=true to ensure draft is created if it doesn't exist
  return setDocument(collectionPath, draftId, updateData, true);
}

export async function deleteDraftEntry(companyId: string, wellId: string, draftId: string) {
  const collectionPath = `companies/${companyId}/wells/${wellId}/draft_entries`;
  return deleteDocument(collectionPath, draftId);
}

// ==================== ROUTES API ====================

export async function fetchRoutes(companyId: string, userId?: string) {
  const filters: any[] = [{ field: 'companyId', operator: '==', value: companyId }];
  if (userId) {
    filters.push({ field: 'userId', operator: '==', value: userId });
  }
  return queryDocuments('routes', filters, 'date', 'desc');
}

export async function createRoute(companyId: string, routeData: any) {
  return createDocument('routes', { ...routeData, companyId });
}

export async function updateRoute(routeId: string, routeData: any) {
  return updateDocument('routes', routeId, routeData);
}

// ==================== BILLING API ====================

export async function fetchBillingData(companyId: string) {
  return getDocument('billing', companyId);
}

export async function updateBillingData(companyId: string, billingData: any) {
  return updateDocument('billing', companyId, billingData);
}

// ==================== ANALYTICS API ====================

export async function fetchAnalytics(companyId: string, startDate?: string, endDate?: string) {
  return callFunction('getAnalytics', { companyId, startDate, endDate });
}

// ==================== LEGACY/COMPATIBILITY API ====================

/**
 * Generic fetch function for backwards compatibility
 * Replaces fetch calls to Supabase Edge Functions
 */
export async function apiFetch(endpoint: string, options: RequestInit = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  // Parse endpoint to determine which Firebase operation to use
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...options.headers
  };

  // For now, throw error - components should be updated to use specific API methods
  console.error('Legacy apiFetch called. Update component to use specific API methods.');
  throw new Error('Legacy API not supported. Use specific Firebase API methods.');
}

export default {
  // Wells
  fetchWells,
  createWell,
  updateWell,
  deleteWell,
  getWellById,

  // Production
  fetchProductionData,
  createProductionEntry,
  updateProductionEntry,
  deleteProductionEntry,
  createWellEntry,
  fetchWellEntries,
  upsertWellEntry,
  fetchWellEntryByDate,

  // Haul History
  fetchHaulHistory,
  getHaulTotals,
  createHaulHistoryEntry,

  // Users
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,

  // Notes
  fetchWellNotes,
  createWellNote,

  // Invoices
  fetchInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  approveInvoice,

  // Notifications
  fetchNotifications,
  createNotification,
  markNotificationAsRead,
  deleteNotification,

  // Companies
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,

  // Time Clock
  fetchTimeEntries,
  clockIn,
  clockOut,
  getActiveTimeEntry,

  // Shift Handoffs
  fetchShiftHandoffs,
  getActiveHandoff,
  createShiftHandoff,
  updateShiftHandoff,
  submitShiftHandoff,

  // Tasks
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,

  // Routes
  fetchRoutes,
  createRoute,
  updateRoute,

  // Billing
  fetchBillingData,
  updateBillingData,

  // Drafts
  updateDraftEntryField,
  deleteDraftEntry,

  // Analytics
  fetchAnalytics
};
